import { registerBlockType } from "@wordpress/blocks";
import {
    useBlockProps,
    InspectorControls,
    InnerBlocks,
    RichText,
    PanelColorSettings,
} from "@wordpress/block-editor";
import {
    PanelBody,
    TextControl,
    SelectControl,
    ToggleControl,
    Button,
    __experimentalView as View,
    __experimentalText as Text,
} from "@wordpress/components";

import UnitRange from "./components/UnitRange";

const ALLOWED_BLOCKS = [
    "core/buttons",
    "core/button",
    "core/paragraph",
    "core/heading",
    "core/image",
    "core/spacer",
    "core/html",
    "core/group",
];

const SUBMIT_BUTTON_TEMPLATE = [
    [
        "core/group",
        { className: "submit-button-container" },
        [
            [
                "core/button",
                {
                    className: "submit-button",
                    text: "Wyślij wiadomość",
                },
            ],
        ],
    ],
];

const ATTACHMENTS_TEMPLATE = [
    [
        "core/button",
        {
            className: "file-upload-button",
            text: "Wybierz pliki",
        },
    ],
];

registerBlockType("danone/contact-form", {
    apiVersion: 2,
    title: "Formularz kontaktowy",
    icon: "email",
    category: "design",
    attributes: {
        endpoint: {
            type: "string",
            default: "UAT",
        },
        showAttachments: {
            type: "boolean",
            default: true,
        },
        maxFileSize: {
            type: "number",
            default: 5,
        },
        allowedFileTypes: {
            type: "array",
            default: ["jpg", "jpeg", "png", "pdf"],
        },
        consents: {
            type: "array",
            default: [
                {
                    text: "",
                    param: "",
                    required: false,
                    fontSize: { value: 14, unit: "px" },
                    textColor: "#000000",
                },
            ],
        },
        fields: {
            type: "array",
            default: [
                {
                    type: "text",
                    label: "Imię i nazwisko",
                    param: "name",
                    required: true,
                    placeholder: "Jan Kowalski",
                    labelFontSize: { value: 14, unit: "px" },
                    labelColor: "#000000",
                },
                {
                    type: "email",
                    label: "Email",
                    param: "email",
                    required: true,
                    placeholder: "jan@example.com",
                    labelFontSize: { value: 14, unit: "px" },
                    labelColor: "#000000",
                },
                {
                    type: "textarea",
                    label: "Wiadomość",
                    param: "message",
                    required: true,
                    placeholder: "Treść wiadomości",
                    labelFontSize: { value: 14, unit: "px" },
                    labelColor: "#000000",
                },
            ],
        },
        attachmentsConfig: {
            type: "object",
            default: {
                label: "Załączniki",
                fontSize: { value: 14, unit: "px" },
                textColor: "#000000",
                infoText:
                    "Maksymalny rozmiar: {maxFileSize}MB. Dozwolone typy: {allowedTypes}",
                buttonText: "Wybierz pliki",
                buttonFontSize: { value: 14, unit: "px" },
                buttonTextColor: "#FFFFFF",
                buttonBgColor: "#0073aa",
                buttonPaddingX: { value: 20, unit: "px" },
                buttonPaddingY: { value: 10, unit: "px" },
                buttonBorderRadius: { value: 4, unit: "px" },
                buttonBorderWidth: { value: 1, unit: "px" },
                buttonBorderColor: "#000000",
                buttonBorderStyle: "solid",
                infoFontSize: { value: 12, unit: "px" },
                buttonMarginTop: { value: 10, unit: "px" },
                buttonMarginBottom: { value: 10, unit: "px" },
            },
        },
        submitButtonContent: {
            type: "string",
            source: "html",
            selector: ".form-submit",
        },
        formConfig: {
            type: "object",
            default: {
                labelFontSize: { value: 14, unit: "px" },
                labelColor: "#000000",
            },
        },
    },

    edit: function ContactFormEdit({ attributes, setAttributes }) {
        const blockProps = useBlockProps();
        const {
            endpoint,
            showAttachments,
            maxFileSize,
            allowedFileTypes,
            consents,
            fields,
            attachmentsConfig,
            formConfig,
        } = attributes;

        const defaultAttachmentsConfig = {
            label: "Załączniki",
            fontSize: { value: 14, unit: "px" },
            textColor: "#000000",
            buttonText: "Wybierz pliki",
            buttonFontSize: { value: 14, unit: "px" },
            buttonTextColor: "#FFFFFF",
            buttonBgColor: "#0073aa",
            buttonPaddingX: { value: 20, unit: "px" },
            buttonPaddingY: { value: 10, unit: "px" },
            buttonBorderRadius: { value: 4, unit: "px" },
            buttonBorderWidth: { value: 1, unit: "px" },
            buttonBorderColor: "#000000",
            buttonBorderStyle: "solid",
            infoText:
                "Maksymalny rozmiar: {maxFileSize}MB. Dozwolone typy: {allowedTypes}",
            infoFontSize: { value: 12, unit: "px" },
            buttonMarginTop: { value: 10, unit: "px" },
            buttonMarginBottom: { value: 10, unit: "px" },
        };

        const safeAttachmentsConfig = {
            ...defaultAttachmentsConfig,
            ...attachmentsConfig,
        };

        const defaultFormConfig = {
            labelFontSize: { value: 14, unit: "px" },
            labelColor: "#000000",
        };

        const safeFormConfig = {
            ...defaultFormConfig,
            ...formConfig,
        };

        const addConsent = () => {
            setAttributes({
                consents: [
                    ...consents,
                    {
                        text: "",
                        param: "",
                        required: false,
                        fontSize: { value: 14, unit: "px" },
                        textColor: "#000000",
                    },
                ],
            });
        };

        const updateConsent = (index, field, value) => {
            const newConsents = [...consents];
            newConsents[index] = { ...newConsents[index], [field]: value };
            setAttributes({ consents: newConsents });
        };

        const removeConsent = (index) => {
            setAttributes({
                consents: consents.filter((_, i) => i !== index),
            });
        };

        const addField = () => {
            setAttributes({
                fields: [
                    ...fields,
                    {
                        type: "text",
                        label: "",
                        param: "",
                        required: false,
                        placeholder: "",
                        labelFontSize: { value: 14, unit: "px" },
                        labelColor: "#000000",
                    },
                ],
            });
        };

        const updateField = (index, field, value) => {
            const newFields = [...fields];
            newFields[index] = { ...newFields[index], [field]: value };
            setAttributes({ fields: newFields });
        };

        const removeField = (index) => {
            setAttributes({
                fields: fields.filter((_, i) => i !== index),
            });
        };

        const updateAttachmentsConfig = (field, value) => {
            setAttributes({
                attachmentsConfig: {
                    ...safeAttachmentsConfig,
                    [field]: value,
                },
            });
        };

        const updateFormConfig = (field, value) => {
            setAttributes({
                formConfig: {
                    ...safeFormConfig,
                    [field]: value,
                },
            });
        };

        const buttonStyles = {
            fontSize: safeAttachmentsConfig?.buttonFontSize?.value
                ? `${safeAttachmentsConfig.buttonFontSize.value}${safeAttachmentsConfig.buttonFontSize.unit}`
                : "14px",
            color: safeAttachmentsConfig?.buttonTextColor || "#FFFFFF",
            backgroundColor: safeAttachmentsConfig?.buttonBgColor || "#0073aa",
            padding: `${safeAttachmentsConfig?.buttonPaddingY?.value || 10}${
                safeAttachmentsConfig?.buttonPaddingY?.unit || "px"
            } ${safeAttachmentsConfig?.buttonPaddingX?.value || 20}${
                safeAttachmentsConfig?.buttonPaddingX?.unit || "px"
            }`,
            borderRadius: safeAttachmentsConfig?.buttonBorderRadius?.value
                ? `${safeAttachmentsConfig.buttonBorderRadius.value}${safeAttachmentsConfig.buttonBorderRadius.unit}`
                : "4px",
            borderWidth: safeAttachmentsConfig?.buttonBorderWidth?.value
                ? `${safeAttachmentsConfig.buttonBorderWidth.value}${safeAttachmentsConfig.buttonBorderWidth.unit}`
                : "1px",
            borderColor: safeAttachmentsConfig?.buttonBorderColor || "#000000",
            borderStyle: safeAttachmentsConfig?.buttonBorderStyle || "solid",
            marginTop: safeAttachmentsConfig?.buttonMarginTop?.value
                ? `${safeAttachmentsConfig.buttonMarginTop.value}${safeAttachmentsConfig.buttonMarginTop.unit}`
                : "10px",
            marginBottom: safeAttachmentsConfig?.buttonMarginBottom?.value
                ? `${safeAttachmentsConfig.buttonMarginBottom.value}${safeAttachmentsConfig.buttonMarginBottom.unit}`
                : "10px",
        };

        return (
            <>
                <InspectorControls>
                    <PanelBody title="Ustawienia formularza" initialOpen={true}>
                        <View style={{ padding: "15px 0 0 0" }}>
                            <SelectControl
                                label="Środowisko"
                                value={endpoint}
                                options={[
                                    { label: "UAT", value: "UAT" },
                                    { label: "PROD", value: "PROD" },
                                ]}
                                onChange={(value) =>
                                    setAttributes({ endpoint: value })
                                }
                            />
                        </View>

                        <View style={{ padding: "10px 0" }}>
                            <ToggleControl
                                label="Załączniki"
                                checked={showAttachments}
                                onChange={(value) =>
                                    setAttributes({ showAttachments: value })
                                }
                                __nextHasNoMarginBottom
                            />
                        </View>

                        {showAttachments && (
                            <View style={{ padding: "10px 0" }}>
                                <TextControl
                                    type="number"
                                    label="Maksymalny rozmiar pliku (MB)"
                                    value={maxFileSize}
                                    onChange={(value) =>
                                        setAttributes({
                                            maxFileSize: parseInt(value),
                                        })
                                    }
                                />
                                <TextControl
                                    label="Dozwolone typy plików"
                                    help="Oddziel typy przecinkami (np. jpg,pdf)"
                                    value={allowedFileTypes.join(",")}
                                    onChange={(value) =>
                                        setAttributes({
                                            allowedFileTypes: value
                                                .split(",")
                                                .map((t) => t.trim()),
                                        })
                                    }
                                />
                            </View>
                        )}
                    </PanelBody>

                    <PanelBody title="Pola formularza" initialOpen={false}>
                        {fields.map((field, index) => (
                            <View
                                key={index}
                                style={{
                                    marginTop: index === 0 ? "0" : "40px",
                                    padding: "20px",
                                    backgroundColor: "#f8f8f8",
                                    borderRadius: "0.5rem",
                                }}
                            >
                                <View style={{ padding: "0 0 15px 0" }}>
                                    <SelectControl
                                        label="Typ pola"
                                        value={field.type}
                                        options={[
                                            { label: "Tekst", value: "text" },
                                            { label: "Email", value: "email" },
                                            {
                                                label: "Obszar tekstowy",
                                                value: "textarea",
                                            },
                                        ]}
                                        onChange={(value) =>
                                            updateField(index, "type", value)
                                        }
                                    />
                                </View>

                                <View style={{ padding: "0 0 15px 0" }}>
                                    <TextControl
                                        label="Etykieta"
                                        value={field.label}
                                        onChange={(value) =>
                                            updateField(index, "label", value)
                                        }
                                    />
                                </View>

                                <View style={{ padding: "0 0 15px 0" }}>
                                    <UnitRange
                                        label="Rozmiar czcionki etykiety"
                                        rangeValue={
                                            field.labelFontSize?.value || 14
                                        }
                                        unitValue={
                                            field.labelFontSize || {
                                                value: 14,
                                                unit: "px",
                                            }
                                        }
                                        onRangeChange={(value) =>
                                            updateField(
                                                index,
                                                "labelFontSize",
                                                {
                                                    ...(field.labelFontSize || {
                                                        unit: "px",
                                                    }),
                                                    value,
                                                }
                                            )
                                        }
                                        onUnitChange={(value) =>
                                            updateField(
                                                index,
                                                "labelFontSize",
                                                value
                                            )
                                        }
                                        min={8}
                                        max={32}
                                        __nextHasNoMarginBottom
                                    />
                                </View>

                                <View style={{ padding: "0 0 15px 0" }}>
                                    <PanelColorSettings
                                        title=""
                                        colorSettings={[
                                            {
                                                value: field.labelColor,
                                                onChange: (value) =>
                                                    updateField(
                                                        index,
                                                        "labelColor",
                                                        value
                                                    ),
                                                label: "Kolor etykiety",
                                            },
                                        ]}
                                    />
                                </View>

                                <Button
                                    onClick={() => removeField(index)}
                                    className="components-button is-destructive"
                                    style={{ width: "100%" }}
                                >
                                    Usuń pole
                                </Button>
                            </View>
                        ))}

                        <View style={{ marginTop: "20px" }}>
                            <Button
                                onClick={addField}
                                className="components-button is-primary"
                                style={{
                                    width: "100%",
                                    borderRadius: "0.5rem",
                                }}
                            >
                                Dodaj nowe pole
                            </Button>
                        </View>
                    </PanelBody>

                    <PanelBody title="Zgody" initialOpen={false}>
                        {consents.map((consent, index) => (
                            <View
                                key={index}
                                style={{
                                    marginTop: index === 0 ? "0" : "40px",
                                    padding: "20px",
                                    backgroundColor: "#f8f8f8",
                                    borderRadius: "0.5rem",
                                }}
                            >
                                <View style={{ marginBottom: "20px" }}>
                                    <UnitRange
                                        label="Rozmiar tekstu"
                                        rangeValue={
                                            consent.fontSize?.value || 14
                                        }
                                        unitValue={
                                            consent.fontSize || {
                                                value: 14,
                                                unit: "px",
                                            }
                                        }
                                        onRangeChange={(value) =>
                                            updateConsent(index, "fontSize", {
                                                ...(consent.fontSize || {
                                                    unit: "px",
                                                }),
                                                value,
                                            })
                                        }
                                        onUnitChange={(value) =>
                                            updateConsent(
                                                index,
                                                "fontSize",
                                                value
                                            )
                                        }
                                        min={8}
                                        max={32}
                                    />
                                </View>

                                <View style={{ marginBottom: "20px" }}>
                                    <PanelColorSettings
                                        title=""
                                        colorSettings={[
                                            {
                                                value: consent.textColor,
                                                onChange: (value) =>
                                                    updateConsent(
                                                        index,
                                                        "textColor",
                                                        value
                                                    ),
                                                label: "Kolor tekstu",
                                            },
                                        ]}
                                    />
                                </View>

                                <View style={{ marginBottom: "15px" }}>
                                    <ToggleControl
                                        label="Wymagane"
                                        checked={consent.required}
                                        onChange={(value) =>
                                            updateConsent(
                                                index,
                                                "required",
                                                value
                                            )
                                        }
                                    />
                                </View>

                                <Button
                                    onClick={() => removeConsent(index)}
                                    className="components-button is-destructive"
                                    style={{ width: "100%", paddingLeft: 0 }}
                                >
                                    Usuń zgodę
                                </Button>

                                <View
                                    style={{
                                        marginTop: "20px",
                                        marginBottom: "20px",
                                    }}
                                >
                                    <Text
                                        as="h4"
                                        style={{
                                            margin: "0",
                                            padding: "0 0 15px 0",
                                            fontSize: "11px",
                                            fontWeight: "500",
                                        }}
                                    >
                                        Treść zgody
                                    </Text>
                                    <RichText
                                        tagName="div"
                                        label="Treść zgody"
                                        value={consent.text}
                                        onChange={(value) =>
                                            updateConsent(index, "text", value)
                                        }
                                        placeholder="Wprowadź treść zgody..."
                                        style={{
                                            padding: "8px",
                                            backgroundColor: "#fff",
                                            border: "1px solid #ddd",
                                            borderRadius: "4px",
                                            minHeight: "80px",
                                            fontSize: consent.fontSize
                                                ? `${consent.fontSize.value}${consent.fontSize.unit}`
                                                : "14px",
                                            color:
                                                consent.textColor || "#000000",
                                        }}
                                    />
                                </View>
                            </View>
                        ))}

                        <View style={{ marginTop: "20px" }}>
                            <Button
                                onClick={addConsent}
                                className="components-button is-primary"
                                style={{
                                    width: "100%",
                                    borderRadius: "0.5rem",
                                }}
                            >
                                Dodaj nową zgodę
                            </Button>
                        </View>
                    </PanelBody>

                    {showAttachments && (
                        <PanelBody
                            title="Ustawienia załączników"
                            initialOpen={false}
                        >
                            <View style={{ padding: "15px 0 0 0" }}>
                                <TextControl
                                    label="Etykieta sekcji"
                                    value={safeAttachmentsConfig.label}
                                    onChange={(value) =>
                                        updateAttachmentsConfig("label", value)
                                    }
                                />
                            </View>

                            <View style={{ padding: "10px 0" }}>
                                <TextControl
                                    label="Tekst przycisku"
                                    value={safeAttachmentsConfig.buttonText}
                                    onChange={(value) =>
                                        updateAttachmentsConfig(
                                            "buttonText",
                                            value
                                        )
                                    }
                                />
                            </View>

                            <View style={{ padding: "10px 0" }}>
                                <TextControl
                                    label="Tekst informacyjny"
                                    value={safeAttachmentsConfig.infoText}
                                    onChange={(value) =>
                                        updateAttachmentsConfig(
                                            "infoText",
                                            value
                                        )
                                    }
                                />
                            </View>

                            <View style={{ padding: "10px 0" }}>
                                <UnitRange
                                    label="Rozmiar tekstu etykiety"
                                    rangeValue={
                                        safeAttachmentsConfig.fontSize.value
                                    }
                                    unitValue={safeAttachmentsConfig.fontSize}
                                    onRangeChange={(value) =>
                                        updateAttachmentsConfig("fontSize", {
                                            ...safeAttachmentsConfig.fontSize,
                                            value,
                                        })
                                    }
                                    onUnitChange={(value) =>
                                        updateAttachmentsConfig(
                                            "fontSize",
                                            value
                                        )
                                    }
                                    min={8}
                                    max={32}
                                    __nextHasNoMarginBottom
                                />
                            </View>

                            <View style={{ padding: "10px 0" }}>
                                <UnitRange
                                    label="Rozmiar tekstu przycisku"
                                    rangeValue={
                                        safeAttachmentsConfig.buttonFontSize
                                            .value
                                    }
                                    unitValue={
                                        safeAttachmentsConfig.buttonFontSize
                                    }
                                    onRangeChange={(value) =>
                                        updateAttachmentsConfig(
                                            "buttonFontSize",
                                            {
                                                ...safeAttachmentsConfig.buttonFontSize,
                                                value,
                                            }
                                        )
                                    }
                                    onUnitChange={(value) =>
                                        updateAttachmentsConfig(
                                            "buttonFontSize",
                                            value
                                        )
                                    }
                                    min={8}
                                    max={32}
                                    __nextHasNoMarginBottom
                                />
                            </View>

                            <View style={{ padding: "10px 0" }}>
                                <UnitRange
                                    label="Rozmiar tekstu informacyjnego"
                                    rangeValue={
                                        safeAttachmentsConfig.infoFontSize.value
                                    }
                                    unitValue={
                                        safeAttachmentsConfig.infoFontSize
                                    }
                                    onRangeChange={(value) =>
                                        updateAttachmentsConfig(
                                            "infoFontSize",
                                            {
                                                ...safeAttachmentsConfig.infoFontSize,
                                                value,
                                            }
                                        )
                                    }
                                    onUnitChange={(value) =>
                                        updateAttachmentsConfig(
                                            "infoFontSize",
                                            value
                                        )
                                    }
                                    min={8}
                                    max={24}
                                    __nextHasNoMarginBottom
                                />
                            </View>

                            <View style={{ padding: "10px 0" }}>
                                <PanelColorSettings
                                    title="Kolory"
                                    enableAlpha={true}
                                    colorSettings={[
                                        {
                                            value: safeAttachmentsConfig.textColor,
                                            onChange: (value) =>
                                                updateAttachmentsConfig(
                                                    "textColor",
                                                    value
                                                ),
                                            label: "Kolor tekstu etykiety",
                                        },
                                        {
                                            value: safeAttachmentsConfig.buttonTextColor,
                                            onChange: (value) =>
                                                updateAttachmentsConfig(
                                                    "buttonTextColor",
                                                    value
                                                ),
                                            label: "Kolor tekstu przycisku",
                                        },
                                        {
                                            value: safeAttachmentsConfig.buttonBgColor,
                                            onChange: (value) =>
                                                updateAttachmentsConfig(
                                                    "buttonBgColor",
                                                    value
                                                ),
                                            label: "Kolor tła przycisku",
                                        },
                                    ]}
                                />
                            </View>

                            <PanelBody
                                title="Ustawienia przycisku"
                                initialOpen={false}
                            >
                                <View style={{ padding: "15px 0 0 0" }}>
                                    <UnitRange
                                        label="Padding poziomy"
                                        rangeValue={
                                            safeAttachmentsConfig.buttonPaddingX
                                                .value
                                        }
                                        unitValue={
                                            safeAttachmentsConfig.buttonPaddingX
                                        }
                                        onRangeChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonPaddingX",
                                                {
                                                    ...safeAttachmentsConfig.buttonPaddingX,
                                                    value,
                                                }
                                            )
                                        }
                                        onUnitChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonPaddingX",
                                                value
                                            )
                                        }
                                        min={0}
                                        max={100}
                                        __nextHasNoMarginBottom
                                    />
                                </View>

                                <View style={{ padding: "10px 0" }}>
                                    <UnitRange
                                        label="Padding pionowy"
                                        rangeValue={
                                            safeAttachmentsConfig.buttonPaddingY
                                                .value
                                        }
                                        unitValue={
                                            safeAttachmentsConfig.buttonPaddingY
                                        }
                                        onRangeChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonPaddingY",
                                                {
                                                    ...safeAttachmentsConfig.buttonPaddingY,
                                                    value,
                                                }
                                            )
                                        }
                                        onUnitChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonPaddingY",
                                                value
                                            )
                                        }
                                        min={0}
                                        max={100}
                                        __nextHasNoMarginBottom
                                    />
                                </View>

                                <View style={{ padding: "10px 0" }}>
                                    <UnitRange
                                        label="Margines górny"
                                        rangeValue={
                                            safeAttachmentsConfig
                                                .buttonMarginTop.value
                                        }
                                        unitValue={
                                            safeAttachmentsConfig.buttonMarginTop
                                        }
                                        onRangeChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonMarginTop",
                                                {
                                                    ...safeAttachmentsConfig.buttonMarginTop,
                                                    value,
                                                }
                                            )
                                        }
                                        onUnitChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonMarginTop",
                                                value
                                            )
                                        }
                                        min={0}
                                        max={100}
                                        __nextHasNoMarginBottom
                                    />
                                </View>

                                <View style={{ padding: "10px 0" }}>
                                    <UnitRange
                                        label="Margines dolny"
                                        rangeValue={
                                            safeAttachmentsConfig
                                                .buttonMarginBottom.value
                                        }
                                        unitValue={
                                            safeAttachmentsConfig.buttonMarginBottom
                                        }
                                        onRangeChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonMarginBottom",
                                                {
                                                    ...safeAttachmentsConfig.buttonMarginBottom,
                                                    value,
                                                }
                                            )
                                        }
                                        onUnitChange={(value) =>
                                            updateAttachmentsConfig(
                                                "buttonMarginBottom",
                                                value
                                            )
                                        }
                                        min={0}
                                        max={100}
                                        __nextHasNoMarginBottom
                                    />
                                </View>
                            </PanelBody>
                        </PanelBody>
                    )}
                </InspectorControls>

                <div {...blockProps}>
                    <form
                        className="contact-form-preview"
                        onClick={(e) => e.preventDefault()}
                    >
                        {fields.map((field, index) => (
                            <div key={index} className="form-group">
                                <label
                                    style={{
                                        fontSize: field.labelFontSize
                                            ? `${field.labelFontSize.value}${field.labelFontSize.unit}`
                                            : "14px",
                                        color: field.labelColor || "#000000",
                                    }}
                                >
                                    {field.label}
                                    {field.required && (
                                        <span className="required">*</span>
                                    )}
                                </label>
                                {field.type === "textarea" ? (
                                    <textarea
                                        className="form-control"
                                        disabled
                                        placeholder={field.placeholder}
                                    ></textarea>
                                ) : (
                                    <input
                                        type={field.type}
                                        className="form-control"
                                        disabled
                                        placeholder={field.placeholder}
                                    />
                                )}
                                <div className="form-feedback"></div>
                            </div>
                        ))}
                        {showAttachments && (
                            <div className="form-group attachments-group">
                                <label
                                    style={{
                                        fontSize: safeAttachmentsConfig
                                            ?.fontSize?.value
                                            ? `${safeAttachmentsConfig.fontSize.value}${safeAttachmentsConfig.fontSize.unit}`
                                            : "14px",
                                        color:
                                            safeAttachmentsConfig?.textColor ||
                                            "#000000",
                                    }}
                                >
                                    {safeAttachmentsConfig?.label ||
                                        "Załączniki"}
                                </label>
                                <div className="file-upload-wrapper">
                                    <div className="file-input-container">
                                        <button
                                            type="button"
                                            style={buttonStyles}
                                            className="file-upload-button"
                                            disabled
                                        >
                                            {safeAttachmentsConfig?.buttonText ||
                                                "Wybierz pliki"}
                                        </button>
                                    </div>
                                    <div className="file-upload-info">
                                        <div className="selected-files"></div>
                                        <small
                                            style={{
                                                fontSize: safeAttachmentsConfig
                                                    ?.infoFontSize?.value
                                                    ? `${safeAttachmentsConfig.infoFontSize.value}${safeAttachmentsConfig.infoFontSize.unit}`
                                                    : "12px",
                                                color:
                                                    safeAttachmentsConfig?.textColor ||
                                                    "#000000",
                                            }}
                                        >
                                            {safeAttachmentsConfig?.infoText
                                                .replace(
                                                    "{maxFileSize}",
                                                    maxFileSize
                                                )
                                                .replace(
                                                    "{allowedTypes}",
                                                    allowedFileTypes.join(", ")
                                                )}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        )}
                        {consents.length > 0 && (
                            <div className="form-group consents-group">
                                {consents.map((consent, index) => (
                                    <div
                                        key={index}
                                        className="consent-item"
                                        style={{
                                            marginBottom: "20px",
                                            fontSize: consent.fontSize
                                                ? `${consent.fontSize.value}${consent.fontSize.unit}`
                                                : "14px",
                                            color:
                                                consent.textColor || "#000000",
                                        }}
                                    >
                                        <label>
                                            <input
                                                type="checkbox"
                                                name={`consent_${consent.param}`}
                                                required={consent.required}
                                            />
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: `${
                                                        consent.required
                                                            ? '<span class="required">*</span>'
                                                            : ""
                                                    }${consent.text}`,
                                                }}
                                            ></span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="form-submit">
                            <InnerBlocks
                                allowedBlocks={ALLOWED_BLOCKS}
                                template={SUBMIT_BUTTON_TEMPLATE}
                                templateLock={false}
                                renderAppender={() => (
                                    <InnerBlocks.ButtonBlockAppender />
                                )}
                            />
                        </div>
                    </form>
                </div>
            </>
        );
    },

    save: function ContactFormSave({ attributes }) {
        const blockProps = useBlockProps.save();
        const {
            endpoint,
            showAttachments,
            maxFileSize,
            allowedFileTypes,
            consents,
            fields,
            attachmentsConfig,
            formConfig,
        } = attributes;

        const buttonStyles = {
            fontSize: attachmentsConfig?.buttonFontSize?.value
                ? `${attachmentsConfig.buttonFontSize.value}${attachmentsConfig.buttonFontSize.unit}`
                : "14px",
            color: attachmentsConfig?.buttonTextColor || "#FFFFFF",
            backgroundColor: attachmentsConfig?.buttonBgColor || "#0073aa",
            padding: `${attachmentsConfig?.buttonPaddingY?.value || 10}${
                attachmentsConfig?.buttonPaddingY?.unit || "px"
            } ${attachmentsConfig?.buttonPaddingX?.value || 20}${
                attachmentsConfig?.buttonPaddingX?.unit || "px"
            }`,
            borderRadius: attachmentsConfig?.buttonBorderRadius?.value
                ? `${attachmentsConfig.buttonBorderRadius.value}${attachmentsConfig.buttonBorderRadius.unit}`
                : "4px",
            borderWidth: attachmentsConfig?.buttonBorderWidth?.value
                ? `${attachmentsConfig.buttonBorderWidth.value}${attachmentsConfig.buttonBorderWidth.unit}`
                : "1px",
            borderColor: attachmentsConfig?.buttonBorderColor || "#000000",
            borderStyle: attachmentsConfig?.buttonBorderStyle || "solid",
            marginTop: attachmentsConfig?.buttonMarginTop?.value
                ? `${attachmentsConfig.buttonMarginTop.value}${attachmentsConfig.buttonMarginTop.unit}`
                : "10px",
            marginBottom: attachmentsConfig?.buttonMarginBottom?.value
                ? `${attachmentsConfig.buttonMarginBottom.value}${attachmentsConfig.buttonMarginBottom.unit}`
                : "10px",
        };

        return (
            <div {...blockProps}>
                <form
                    className="contact-form"
                    data-endpoint={endpoint}
                    data-show-attachments={showAttachments}
                    data-max-file-size={maxFileSize}
                    data-allowed-types={allowedFileTypes.join(",")}
                >
                    {fields.map((field, index) => (
                        <div key={index} className="form-group">
                            <label
                                htmlFor={field.param}
                                style={{
                                    fontSize: field.labelFontSize
                                        ? `${field.labelFontSize.value}${field.labelFontSize.unit}`
                                        : "14px",
                                    color: field.labelColor || "#000000",
                                }}
                            >
                                {field.label}
                                {field.required && (
                                    <span className="required">*</span>
                                )}
                            </label>
                            {field.type === "textarea" ? (
                                <textarea
                                    id={field.param}
                                    name={field.param}
                                    className="form-control"
                                    required={field.required}
                                    placeholder={field.placeholder}
                                ></textarea>
                            ) : (
                                <input
                                    type={field.type}
                                    id={field.param}
                                    name={field.param}
                                    className="form-control"
                                    required={field.required}
                                    placeholder={field.placeholder}
                                />
                            )}
                            <div className="form-feedback"></div>
                        </div>
                    ))}
                    {showAttachments && (
                        <div
                            className="form-group attachments-group"
                            data-button-font-size={`${attachmentsConfig?.buttonFontSize?.value}${attachmentsConfig?.buttonFontSize?.unit}`}
                            data-button-text-color={
                                attachmentsConfig?.buttonTextColor
                            }
                            data-button-bg-color={
                                attachmentsConfig?.buttonBgColor
                            }
                            data-button-padding-y={`${attachmentsConfig?.buttonPaddingY?.value}${attachmentsConfig?.buttonPaddingY?.unit}`}
                            data-button-padding-x={`${attachmentsConfig?.buttonPaddingX?.value}${attachmentsConfig?.buttonPaddingX?.unit}`}
                            data-button-border-radius={`${attachmentsConfig?.buttonBorderRadius?.value}${attachmentsConfig?.buttonBorderRadius?.unit}`}
                            data-button-border-width={`${attachmentsConfig?.buttonBorderWidth?.value}${attachmentsConfig?.buttonBorderWidth?.unit}`}
                            data-button-border-color={
                                attachmentsConfig?.buttonBorderColor
                            }
                            data-button-border-style={
                                attachmentsConfig?.buttonBorderStyle
                            }
                            data-button-margin-top={`${attachmentsConfig?.buttonMarginTop?.value}${attachmentsConfig?.buttonMarginTop?.unit}`}
                            data-button-margin-bottom={`${attachmentsConfig?.buttonMarginBottom?.value}${attachmentsConfig?.buttonMarginBottom?.unit}`}
                        >
                            <label
                                style={{
                                    fontSize: attachmentsConfig?.fontSize?.value
                                        ? `${attachmentsConfig.fontSize.value}${attachmentsConfig.fontSize.unit}`
                                        : "14px",
                                    color:
                                        attachmentsConfig?.textColor ||
                                        "#000000",
                                }}
                            >
                                {attachmentsConfig?.label || "Załączniki"}
                            </label>
                            <div className="file-upload-wrapper">
                                <div className="file-input-container">
                                    <input
                                        type="file"
                                        id="attachments"
                                        name="attachments"
                                        multiple
                                        accept={allowedFileTypes
                                            .map((type) => `.${type}`)
                                            .join(",")}
                                    />
                                </div>
                                <div className="file-upload-info">
                                    <div className="selected-files"></div>
                                    <small
                                        style={{
                                            fontSize: attachmentsConfig
                                                ?.infoFontSize?.value
                                                ? `${attachmentsConfig.infoFontSize.value}${attachmentsConfig.infoFontSize.unit}`
                                                : "12px",
                                            color:
                                                attachmentsConfig?.textColor ||
                                                "#000000",
                                        }}
                                    >
                                        {attachmentsConfig?.infoText
                                            .replace(
                                                "{maxFileSize}",
                                                maxFileSize
                                            )
                                            .replace(
                                                "{allowedTypes}",
                                                allowedFileTypes.join(", ")
                                            )}
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}
                    {consents.length > 0 && (
                        <div className="form-group consents-group">
                            {consents.map((consent, index) => (
                                <div
                                    key={index}
                                    className="consent-item"
                                    style={{
                                        marginBottom: "20px",
                                        fontSize: consent.fontSize
                                            ? `${consent.fontSize.value}${consent.fontSize.unit}`
                                            : "14px",
                                        color: consent.textColor || "#000000",
                                    }}
                                >
                                    <label>
                                        <input
                                            type="checkbox"
                                            name={`consent_${consent.param}`}
                                            required={consent.required}
                                        />
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: `${
                                                    consent.required
                                                        ? '<span class="required">*</span>'
                                                        : ""
                                                }${consent.text}`,
                                            }}
                                        ></span>
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="form-submit">
                        <InnerBlocks.Content />
                    </div>
                    <div className="form-message-container"></div>
                </form>
            </div>
        );
    },
});
